import { Info, Moon, Undo2 } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';

type QuietHoursBannerProps = {
    readonly outreach: JoinedOutreachRead;
    readonly organizationId: string | null;
    readonly timezone: string;
    // Revalidate the joined outreach read after the override changes.
    readonly onChanged: () => void | Promise<unknown>;
    readonly className?: string;
};

/**
 * Explains why Phoebe is not calling right now (outbound quiet hours) and
 * offers the outreach-level "Send anyway" override. Rendered only while the
 * hold is active or overridden; the server computes both states
 * (JoinedOutreachRead.quiet_hours_hold) with the same inputs the dispatcher
 * uses, including the case where the next voice round has no attempt row yet.
 */
export function QuietHoursBanner({
    outreach,
    organizationId,
    timezone,
    onChanged,
    className,
}: QuietHoursBannerProps) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const hold = outreach.quiet_hours_hold;
    if (!hold || (!hold.active && !hold.overridden)) {
        return null;
    }

    const setOverride = async (enabled: boolean) => {
        if (!organizationId) {
            return;
        }
        setIsSaving(true);
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/outreaches/{outreach_id}/quiet-hours-override',
                pathParams: {
                    organization_id: organizationId,
                    outreach_id: outreach.id,
                },
                body: { enabled },
            });
            toast.success(
                enabled
                    ? 'Quiet hours overridden — calls will go out now'
                    : 'Quiet hours re-enabled for this outreach',
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
            setConfirmOpen(false);
            await onChanged();
        } catch (error) {
            const apiError = error as { body?: { detail?: string } };
            toast.error(
                apiError?.body?.detail ?? 'Failed to update quiet hours',
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
            console.error('Error updating quiet hours override:', error);
        } finally {
            setIsSaving(false);
        }
    };

    if (hold.overridden) {
        return (
            <div
                className={cn(
                    'bg-muted/50 flex items-center justify-between gap-4 rounded-lg border p-3',
                    className,
                )}
                role="note"
                data-testid="quiet-hours-overridden-banner"
            >
                <div className="flex items-center gap-2.5">
                    <Moon
                        className="text-muted-foreground h-4 w-4 flex-shrink-0"
                        aria-hidden={true}
                    />
                    <p className="text-sm">
                        Quiet hours are overridden for this outreach — calls go
                        out at any hour until it finishes.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isSaving}
                    onClick={() => setOverride(false)}
                    className="flex-shrink-0"
                >
                    {isSaving ? <Spinner className="h-3 w-3" /> : <Undo2 />}
                    Undo
                </Button>
            </div>
        );
    }

    const pausedUntilText = hold.resumes_at
        ? `Phoebe’s calls are paused until ${formatTime(hold.resumes_at, timezone)} (quiet hours) — texts still send.`
        : 'Phoebe’s calls are paused during quiet hours — texts still send.';

    return (
        <Fragment>
            <div
                className={cn(
                    'bg-muted/50 flex items-center justify-between gap-4 rounded-lg border p-3',
                    className,
                )}
                role="note"
                data-testid="quiet-hours-banner"
            >
                <div className="flex items-center gap-2.5">
                    <Moon
                        className="text-muted-foreground h-4 w-4 flex-shrink-0"
                        aria-hidden={true}
                    />
                    <p className="text-sm">{pausedUntilText}</p>
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <Info
                                className="text-muted-foreground h-3.5 w-3.5 flex-shrink-0"
                                data-testid="quiet-hours-settings-hint"
                            />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                You can change them in Settings → Quiet hours.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmOpen(true)}
                    className="flex-shrink-0"
                    data-testid="quiet-hours-send-anyway"
                >
                    Send anyway
                </Button>
            </div>
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Call caregivers during quiet hours?
                        </DialogTitle>
                        <DialogDescription>
                            Phoebe will call caregivers now, during this
                            organization’s quiet hours. Later follow-up calls
                            for this outreach will also skip quiet hours until
                            it finishes.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setConfirmOpen(false)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={() => setOverride(true)}
                            disabled={isSaving}
                            data-testid="quiet-hours-confirm-call-anyway"
                        >
                            {isSaving ? <Spinner className="h-3 w-3" /> : null}
                            Call anyway
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
}
